// =====================================================
// 产品族卡片组件
// =====================================================

import React from 'react';
import { ChevronRight, Boxes, Star, Edit2, Trash2 } from 'lucide-react';
import type { ProductFamily } from '../../types/hierarchy';

interface FamilyCardProps {
  family: ProductFamily;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function FamilyCard({ family, onClick, onEdit, onDelete }: FamilyCardProps) {
  return (
    <div
      className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer"
      onClick={onClick}
    >
      {/* 图片区域 */}
      <div className="aspect-video bg-gray-900 relative">
        {family.image_url ? (
          <img
            src={family.image_url}
            alt={family.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Boxes className="w-12 h-12 text-gray-600" />
          </div>
        )}
        
        {/* 推荐标签 */}
        {family.is_featured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-yellow-400 text-yellow-900 rounded-md text-xs font-medium">
            <Star className="w-3 h-3 fill-yellow-900" />
            推荐
          </div>
        )}
        
        {/* 工艺类型 */}
        {family.process_type && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 text-white rounded-md text-xs">
            {family.process_type}
          </div>
        )}
        
        {/* 悬停遮罩 */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <ChevronRight className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      
      {/* 信息区域 */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{family.name}</h3>
            {family.name_en && (
              <p className="text-sm text-gray-500 truncate">{family.name_en}</p>
            )}
          </div>
          
          {/* 操作按钮 */}
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-gray-400" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Slogan */}
        {family.slogan && (
          <p className="text-sm text-gray-400 italic truncate mt-1">"{family.slogan}"</p>
        )}
        
        {/* 统计信息 */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{family.model_count || 0} 型号</span>
            <span>{family.sku_count || 0} SKUs</span>
          </div>
          
          {/* Pitch Range */}
          {family.pitch_range && (
            <span className="text-xs font-medium text-blue-600">
              {family.pitch_range}
            </span>
          )}
        </div>
        
        {/* 特性标签 */}
        {family.features && family.features.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {family.features.slice(0, 3).map((feature, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {feature}
              </span>
            ))}
            {family.features.length > 3 && (
              <span className="px-2 py-0.5 text-gray-400 text-xs">
                +{family.features.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilyCard;
